import { tinaClient, useTina, useGetCollection, tinaField, useIsEditing } from "@/utils";
import { Page, Header, Section, Button, Markdown, Image, Card } from "@/components";
import { ChevronRight } from "lucide-react";
import Link from "next/link";

export default function BlogPost({ blogPostQuery, blogListQuery }) {
  const { data } = useTina({
    ...blogPostQuery,
  });

  const post = data?.post;
  const title = post?.title;
  const summary = post?.summary;
  const date = post?.date;

  const seo = {
    title: `${title} | Blog`,
    description: summary,
    image: post?.image,
    keywords: post?.keywords,
    article: true,
  };

  const blogPostsToShow = useGetCollection(blogListQuery?.data?.postConnection);
  const position = blogPostsToShow.findIndex((p) => p.id === post.id);
  let nextPosts = blogPostsToShow.filter((p) => p.id !== post.id);
  const itemsToMove = nextPosts.splice(position);

  nextPosts.unshift(...itemsToMove);

  return (
    <Page seo={seo} className="mb-24">
      <Header
        header={{ title: title, subtitle: summary, date: date, post: post }}
        breadCrumb="Articles"
        variant="collection-item"
      />

      <Section className="mt-12 mb-16 lg:mb-24 space-y-4 lg:space-y-8 relative">
        {!post?.published && (
          <div className="absolute w-full h-full -top-24 left-0 z-20 flex justify-center pointer-events-none">
            <div className="text-[100px] font-title font-semibold rotate-[35deg] origin-center h-10 text-black/20">
              Draft
            </div>
          </div>
        )}
        <Image
          src={post?.image}
          alt={title}
          className="w-full h-[320px] md:h-[440px] lg:h-[540px] rounded-lg"
          width={1276}
          height={540}
        />
        <div className="max-w-[780px] mx-auto" data-tina-field={tinaField(post, "body")}>
          <Markdown content={post?.body} />
        </div>
      </Section>

      <Section className="border-t border-dark/30 pt-12 lg:pt-16 space-y-8 lg:space-y-12">
        <div className="flex justify-between items-center">
          <div className="h3">More Articles</div>
          <Button variant="link" asChild>
            <Link href="/blog" aria-label="View All Articles">
              View All Articles
              <ChevronRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          {nextPosts.slice(0, 3).map((post, index) => (
            <Card
              key={post?.title}
              href={`/blog/${post?._sys.filename}`}
              title={post?.title}
              image={post?.image}
              description={summary}
              button={{
                label: "Read Article",
                link: `/blog/${post?._sys.filename}`,
              }}
            />
          ))}
        </div>
      </Section>
    </Page>
  );
}

export const getStaticPaths = async () => {
  const { data } = await tinaClient.queries.postConnection();
  const paths = data.postConnection.edges.map((x) => {
    return { params: { slug: x.node._sys.filename } };
  });

  return {
    paths,
    fallback: "blocking",
  };
};

export const getStaticProps = async (ctx) => {
  const blogPostQuery = await tinaClient.queries.post({
    relativePath: ctx.params.slug + ".mdx",
  });

  let filter = {
    published: { eq: true },
  };

  if (ctx.preview) {
    filter = {};
  }

  const blogListQuery = await tinaClient.queries.blogList({
    filter: filter,
  });

  return {
    notFound: !blogPostQuery?.data?.post?.published && !ctx.preview,
    props: {
      blogPostQuery,
      blogListQuery,
    },
  };
};
